// The reads under `consoleReducer`, and the effects that run them (#231, #258).
//
// Every decision is in the reducer; every call to the API is in one of the
// `read*Into` functions below. They take the dispatch rather than closing over
// a hook, so a test drives them with a fake `Api` and a recording dispatch —
// which is how the 401-from-the-run-list path is held, and it is the path with
// the most in it.
//
// The hook is then the thin part: an effect per read, cancelled by a `live`
// flag on unmount so a late answer cannot dispatch into an unmounted tree, in
// the shape `useRunProjection` already uses.
//
// The actions reject rather than dispatch on failure. A wrong password is the
// sign-in form's business and belongs beside the field; only the answers that
// move the gate — a session, a challenge, a sign-out — come back as events.

import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import {
  cancelConnection,
  checkConnection,
  disconnectAccount,
  readAccounts,
  relaunch,
  renameAccount,
  startConnection,
  type Connection,
  type Started,
} from "../api/accounts";
import {
  ACCOUNT_DELETE_PURPOSE,
  apiKeyActions,
  cancelDeletion,
  changePassword,
  deletionTimeOf,
  deletionPendingOf,
  readWouldDeleteAt,
  requestDeletion,
  sessionActions,
  type ApiKeyActions,
  type SessionActions,
} from "../api/account";
import { authenticatorActions, type AuthenticatorActions } from "../api/authenticator";
import { answerChange, readChanges, type ChangeAnswer } from "../api/changes";
import { readSpend } from "../api/billing";
import { answerConsent, readConsent, type ConsentAnswer, type ConsentOutcome } from "../api/consent";
import { ApiError, browserApi, detailOf, messageOf, type Api } from "../api/http";
import {
  beginPasskeyAuthentication,
  beginPasskeyLogin,
  mintStepUpGrant,
  passkeyActions,
  readStepUpFactors,
  type PasskeyActions,
  type PasskeyChallenge,
  type StepUpFactors,
} from "../api/passkeys";
import {
  createOrg,
  inviteCustomer,
  inviteMember,
  listCustomers,
  listInvites,
  listOrgs,
  listOrgWorkspaces,
  revokeInvite,
  type Capability,
  type Created,
  type OrgWorkspaceRow,
} from "../api/customers";
import { answerGrant, readGrants, requestAgain, revokeGrant } from "../api/grants";
import { readAvailability, startScan, type AvailabilityView } from "../api/posture";
import { readInbox, readOlderAnswered } from "../api/inbox";
import {
  acceptInvite,
  acceptWaitingInvitation,
  listWaitingInvitations,
  previewInvite,
} from "../api/invites";
import { listMembers, readRemoval, removeMember, type MemberRow, type RemovalReach } from "../api/members";
import {
  createIntegration,
  registerWebhook,
  rotateWebhookSecret,
  sendTestDelivery,
  deleteIntegration,
  disableIntegration,
  listIntegrationDeliveries,
  listIntegrations,
  retryDelivery,
  updateIntegration,
  type IntegrationPatch,
  type IntegrationRow,
  type NewIntegration,
  type CreatedWebhook,
  type RotatedWebhook,
  type WebhookConfig,
} from "../api/integrations";
import { listJiraSpaces, listJiraWorkTypes, type JiraLister } from "../api/jiraLists";
import { listSlackChannels, type SlackLister } from "../api/slackChannels";
import { enableConnection, newCheckAfter, savedCheck, sendTestEvent, testConnection } from "./connectionTest";
import { choiceFor, listMemberships } from "../api/memberships";
import { readPolicy, revisePolicy, type PolicyRevision, type StepUp } from "../api/policy";
import { listRoles } from "../api/roles";
import { listRuns } from "../api/runs";
import {
  checkProvider,
  createSource,
  listSources,
  readProvider,
  readSetup,
  readSourceFilter,
  saveSourceFilter,
  setSourceDryRun,
  setProvider,
  setSourceActive,
  setSourceAutoInvestigate,
  sendTestAlert,
  deleteSource,
  type FilterSettings,
  type ProviderCheck,
  type ProviderRow,
  type SourceFilterRead,
  type SourceRow,
  type TestAlertSent,
  type NewSource,
} from "../api/sources";
import {
  dismissHeld,
  investigateHeld,
  readAutomation,
  saveDailyCap,
  type AutomationRead,
} from "../api/automation";
import {
  completeMfa,
  confirmEmailVerification,
  confirmMagicLink,
  hasContext,
  isPlatformViewer,
  readSession,
  register,
  requestEmailVerification,
  requestPasswordReset,
  resetPassword,
  selectContext,
  signIn,
  signOut,
  type ContextChoice,
  type LoginResponse,
  type SessionResponse,
} from "../api/session";
import {
  consoleReducer,
  contextOf,
  initialConsoleState,
  refusalNeedsSessionRead,
  type ConsoleEvent,
  type ConsoleState,
  type Gate,
} from "./consoleState";
import { readIntegrationsStep, useIntegrationsStep } from "./integrationsStep";
import { INBOX_KINDS, inboxViewOf, type DrawerRead } from "./inboxView";
import {
  SETTINGS_SECTIONS,
  currentDestination,
  inLinkContext,
  linkedContextOf,
  routeFor,
  type SettingsSection,
} from "./routes";

export const EXPIRED_NOTICE = "That session has ended. Sign in again.";
export const VERIFIED_MESSAGE = "Your email address is verified.";
export const RESET_MESSAGE = "Your password is changed. Sign in with it.";
export const RESET_ASKED_MESSAGE =
  "If that address has an account, a link to reset its password is on its way. It works for the next hour.";
export const VERIFICATION_ASKED_MESSAGE = "A new verification link is on its way to your inbox.";
export const JOINED_MESSAGE = "You are in. Taking you to the workspace…";
export const NO_TOKEN_MESSAGE =
  "That link is missing its token. Open the link exactly as it was sent.";
export const SIGNED_OUT_NOTICE = "You are signed out.";
export const PASSWORD_CHANGED_NOTICE =
  "Your password is changed, and every session of yours was signed out, this one too. Sign in with the new password.";
export const REFUSED_AGAIN = "The investigations list refused this session. Try again.";

/**
 * What the sign-in page says once a request to delete the account is taken (#1136).
 * The date is the server's `deletes_at`; where it gave none, none is named.
 */
export const deletionRecordedNotice = (deletesAt: string | null): string =>
  deletesAt === null
    ? "You asked for your account to be deleted. To keep it, sign in before it is deleted and choose Cancel deletion."
    : `You asked for your account to be deleted. It will be deleted on ${deletionTimeOf(deletesAt)}. To keep it, sign in before then and choose Cancel deletion.`;

export type Dispatch = (event: ConsoleEvent) => void;

/**
 * `GET /auth/session` as the event it means for the gate (#1136): the session, or that
 * the account is being deleted, which the route says with a 403 naming the date. Any
 * other refusal is thrown, for each caller to read as it did before.
 */
export async function sessionEvent(api: Api): Promise<ConsoleEvent> {
  try {
    return { type: "session", session: await readSession(api) };
  } catch (error) {
    const pending = deletionPendingOf(error);
    if (pending === null) throw error;
    return { type: "deletion-pending", deletesAt: pending.deletesAt };
  }
}

/**
 * `GET /auth/session`, and what each answer means for the gate.
 *
 * No notice on the refusal: a session that was never established in this tab
 * has nothing to tell the person that the sign-in form does not already say.
 */
export async function readSessionInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch(await sessionEvent(api));
  } catch {
    dispatch({ type: "signed-out", notice: null });
  }
}

/**
 * `GET /auth/memberships`, and the selection a list of one makes by itself (#414).
 *
 * The list is read for every signed-in session: before a context is chosen it
 * is the picker, and after one is chosen it is what the rail calls the
 * workspace. **An unselected session holding exactly one membership selects it without
 * being asked** — an existing customer visit survives this read. The picker is a question, and a question with one answer is
 * not worth a screen — and comes back through `GET /auth/session`, which is
 * the one route that says which context the session now reads under. A
 * failure of either read stays on the picker; the gate is left alone.
 */
export async function readMembershipsInto(api: Api, dispatch: Dispatch, autoSelect = true): Promise<void> {
  let memberships;
  try {
    memberships = await listMemberships(api);
  } catch (error) {
    dispatch({ type: "memberships-failed", message: messageOf(error) });
    return;
  }
  dispatch({ type: "memberships", memberships });
  if (!autoSelect || memberships.length !== 1) return;
  try {
    await selectContext(api, choiceFor(memberships[0]!));
    dispatch(await sessionEvent(api));
  } catch (error) {
    dispatch({ type: "memberships-failed", message: messageOf(error) });
  }
}

// ------------------------------------------------------------ the doors --
//
// Each door's work is a function of the api and a dispatch (#415), so a test
// drives the real function against a fake `Api` and renders the real screen on
// the state it produced, as `readRunsInto` is tested. A token is spent exactly
// once: the effect in the hook runs the on-arrival doors (verify, magic) keyed
// on the token, and the form doors run from their submit.

/** The doors the hook's effect acts on when they are reached; the others act from a form. */
const ON_ARRIVAL_DOORS: readonly string[] = ["verify", "magic", "invite"];

/** `/verify?token=`: spend the token on arrival. */
export async function confirmVerificationInto(
  api: Api,
  token: string | null,
  dispatch: Dispatch,
): Promise<void> {
  if (token === null) {
    dispatch({ type: "door", door: { kind: "failed", message: NO_TOKEN_MESSAGE } });
    return;
  }
  dispatch({ type: "door", door: { kind: "busy" } });
  try {
    await confirmEmailVerification(api, token);
    dispatch({ type: "door", door: { kind: "done", message: VERIFIED_MESSAGE, next: "runs" } });
  } catch (error) {
    dispatch({ type: "door", door: { kind: "failed", message: messageOf(error) } });
  }
}

/** `/magic?token=`: the link signs in; the session read after it decides the gate. */
export async function confirmMagicInto(
  api: Api,
  token: string | null,
  dispatch: Dispatch,
): Promise<void> {
  if (token === null) {
    dispatch({ type: "door", door: { kind: "failed", message: NO_TOKEN_MESSAGE } });
    return;
  }
  dispatch({ type: "door", door: { kind: "busy" } });
  try {
    const result = await confirmMagicLink(api, token);
    if (result.mfa_required) {
      dispatch({ type: "mfa", mfaToken: result.mfa_token, methods: result.methods });
      return;
    }
    dispatch(await sessionEvent(api));
    dispatch({ type: "door", door: { kind: "done", message: JOINED_MESSAGE, next: "runs" } });
  } catch (error) {
    dispatch({ type: "door", door: { kind: "failed", message: messageOf(error) } });
  }
}

/**
 * The second factor a sign-in link asked for (#785): the login's own challenge,
 * finished, lands where a link without one lands. A refusal is the challenge
 * screen's to show, as a login's is.
 */
export async function completeMagicMfaInto(
  api: Api,
  challenge: { mfa_token: string; method: string; credential: string },
  dispatch: Dispatch,
): Promise<void> {
  const result = await completeMfa(api, challenge);
  if (result.mfa_required) {
    dispatch({ type: "mfa", mfaToken: result.mfa_token, methods: result.methods });
    return;
  }
  dispatch(await sessionEvent(api));
  dispatch({ type: "door", door: { kind: "done", message: JOINED_MESSAGE, next: "runs" } });
}

/** `/invite?token=`: what the link opens, before anyone acts. */
export async function previewInviteInto(
  api: Api,
  token: string | null,
  dispatch: Dispatch,
): Promise<void> {
  if (token === null) {
    dispatch({ type: "door", door: { kind: "failed", message: NO_TOKEN_MESSAGE } });
    return;
  }
  dispatch({ type: "door", door: { kind: "busy" } });
  try {
    dispatch({
      type: "door",
      door: { kind: "invitation", invite: await previewInvite(api, token) },
    });
  } catch (error) {
    dispatch({ type: "door", door: { kind: "failed", message: messageOf(error) } });
  }
}

/**
 * A signed-in member accepts, and lands **in the inviting workspace**: the
 * membership is created, that context is selected, and the session read back
 * says so. Rejects with the route's refusal, for the form to show.
 */
export async function acceptInviteInto(api: Api, token: string, dispatch: Dispatch): Promise<void> {
  const accepted = await acceptInvite(api, token);
  await selectContext(api, { workspace_id: accepted.workspace_id });
  dispatch(await sessionEvent(api));
  dispatch({ type: "door", door: { kind: "done", message: JOINED_MESSAGE, next: "runs" } });
}

/**
 * `GET /invites/waiting`: what is addressed to this user, wherever it is (#440).
 *
 * Read for every signed-in session, with a context or without one. Without one it
 * is the whole content of the no-workspace screen; with one it is the quiet strip
 * that says a second workspace has asked for them, which is the managed-provider
 * case.
 *
 * **A failure here is the screen's, not the gate's.** An invitee who cannot read
 * this list is still signed in, and the screen says so beside a way to try again —
 * the same terms `runsError` is on.
 */
export async function readWaitingInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "waiting", waiting: await listWaitingInvitations(api) });
  } catch (error) {
    dispatch({ type: "waiting-failed", message: messageOf(error) });
  }
}

/**
 * `GET /api/v1/inbox`: what is waiting for this user to answer, wherever it is (#495).
 *
 * Read for every signed-in session, because the rail counts it on every screen. A
 * failure is the inbox's own, on `readWaitingInto`'s terms.
 */
export async function readInboxInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "inbox", inbox: await readInbox(api) });
  } catch (error) {
    dispatch({ type: "inbox-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/inbox/answered`: one group's older answered items, appended to it (#497). */
export async function readOlderAnsweredInto(
  api: Api,
  group: string,
  cursor: string,
  dispatch: Dispatch,
): Promise<void> {
  try {
    dispatch({ type: "inbox-older", group, cursor, page: await readOlderAnswered(api, group, cursor) });
  } catch (error) {
    dispatch({ type: "inbox-failed", message: messageOf(error) });
  }
}

/**
 * Accept one, and land **in** it: the membership is created, that context is
 * selected, and the session read back says so — the same landing
 * `acceptInviteInto` gives the person who arrived on a link.
 *
 * The memberships go before the session so the rail has the workspace's name on
 * the first frame it is drawn under. Then the list is re-read: an invitation that
 * was just accepted is no longer waiting, and the strip has to stop saying it is.
 *
 * Rejects rather than dispatching on failure, as every act in this file does: the
 * card that was clicked is where the refusal belongs.
 */
export async function acceptWaitingInto(
  api: Api,
  inviteId: string,
  dispatch: Dispatch,
): Promise<void> {
  const accepted = await acceptWaitingInvitation(api, inviteId);
  const choice: ContextChoice =
    accepted.workspace_id !== null ? { workspace_id: accepted.workspace_id } : { org_id: accepted.org_id! };
  await selectContext(api, choice);
  dispatch({ type: "memberships", memberships: await listMemberships(api) });
  dispatch(await sessionEvent(api));
  await readWaitingInto(api, dispatch);
}

/**
 * A stranger registers with the invitation and lands in the inviting
 * workspace the same way. The address is the invitation's, never the form's:
 * the route refuses a mismatch, and the form does not offer one.
 */
export async function registerWithInviteInto(
  api: Api,
  account: { email: string; password: string; display_name: string | null; token: string },
  dispatch: Dispatch,
): Promise<void> {
  const result = await register(api, {
    email: account.email,
    password: account.password,
    display_name: account.display_name,
    invite_token: account.token,
  });
  if (result.mfa_required) {
    dispatch({ type: "mfa", mfaToken: result.mfa_token, methods: result.methods });
    return;
  }
  // The membership the invite created is the only one a new account holds,
  // and it is selected here rather than left to the picker's own rule, so the
  // landing is the workspace and not a question.
  const memberships = await listMemberships(api);
  const invited = memberships.find((m) => m.workspace_id === result.user.selected_workspace_id);
  await selectContext(api, choiceFor(invited ?? memberships[0]!));
  dispatch(await sessionEvent(api));
  dispatch({ type: "memberships", memberships });
  dispatch({ type: "door", door: { kind: "done", message: JOINED_MESSAGE, next: "runs" } });
}

/** `/forgot`: ask for the reset mail. The answer is the same for any address. */
export async function requestResetInto(api: Api, email: string, dispatch: Dispatch): Promise<void> {
  await requestPasswordReset(api, email);
  dispatch({ type: "door", door: { kind: "done", message: RESET_ASKED_MESSAGE, next: null } });
}

/** `/reset?token=`: spend the token with the new password. */
export async function resetPasswordInto(
  api: Api,
  token: string,
  newPassword: string,
  dispatch: Dispatch,
): Promise<void> {
  await resetPassword(api, token, newPassword);
  dispatch({ type: "door", door: { kind: "done", message: RESET_MESSAGE, next: "sign-in" } });
}

/**
 * `GET /api/v1/runs`, and the second read a refusal needs.
 *
 * A 401 or a 409 from the list is not read as the answer — the two routes
 * disagree about which code a contextless session gets (`consoleState.ts`,
 * `refusalNeedsSessionRead`). The session route is asked instead, and it is
 * the one that can say: a refusal there is an ended session and sends the
 * browser back to sign-in; a session that still answers with a context means
 * the list refused for some reason of its own, which the list reports rather
 * than the gate.
 */
export async function readRunsInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "runs", runs: await listRuns(api) });
    return;
  } catch (error) {
    if (!refusalNeedsSessionRead(error)) {
      dispatch({ type: "runs-failed", message: messageOf(error) });
      return;
    }
  }
  try {
    const session = await readSession(api);
    dispatch({ type: "session", session });
    if (hasContext(session)) dispatch({ type: "runs-failed", message: REFUSED_AGAIN });
  } catch {
    dispatch({ type: "signed-out", notice: EXPIRED_NOTICE });
  }
}

/**
 * `GET /auth/roles`, for the settings surface (#258).
 *
 * **A refusal here stays on the section.** The run list's read asks the session
 * route a second time because a 401 or 409 from it cannot be told apart from an
 * ended session, and the whole gate turns on the answer. This read is different
 * in the one way that matters: `Roles.list` refuses a principal with no
 * `workspace_id` (`auth/roles.py`), which is a thing an org-context session *is*
 * rather than a session that has ended — and that session is already past the
 * gate, reading a page it is entitled to. So the refusal is reported where it
 * happened and the gate is left alone.
 */
export async function readRolesInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "roles", roles: await listRoles(api) });
  } catch (error) {
    dispatch({ type: "roles-failed", message: messageOf(error) });
  }
}

/**
 * `GET /api/v1/remediation-policy`, for the settings surface (#260).
 *
 * A refusal stays on the section, for `readRolesInto`'s reason: an org-context
 * session is past the gate and the route answers it 409, which is a fact about
 * the context and not an ended session.
 */
export async function readPolicyInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "policy", policy: await readPolicy(api) });
  } catch (error) {
    dispatch({ type: "policy-failed", message: messageOf(error) });
  }
}

/**
 * The consent screen's two reads, pending requests and standing grants (#263).
 *
 * A refusal stays on the screen, for `readPolicyInto`'s reason: the route answers
 * a session with no workspace 409, which is a fact about the context and not an
 * ended session.
 */
export async function readConsentInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "consent", consent: await readConsent(api) });
  } catch (error) {
    dispatch({ type: "consent-failed", message: messageOf(error) });
  }
}

/**
 * The Changes screen's read, the changes waiting on this session's side (#261).
 *
 * A refusal stays on the screen, for `readConsentInto`'s reason.
 */
export async function readChangesInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "changes", changes: await readChanges(api) });
  } catch (error) {
    dispatch({ type: "changes-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/orgs`, for the platform admin's screen (#417). A refusal stays on it. */
export async function readOrgsInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "orgs", orgs: await listOrgs(api) });
  } catch (error) {
    dispatch({ type: "orgs-failed", message: messageOf(error) });
  }
}

/**
 * Whether the platform's organisation list is read (#417, #701): on the
 * organisations screen for anyone signed in — a non-admin is answered 403 and
 * the screen says so — and on the no-context screen for a platform admin alone,
 * whose chooser it feeds. Never for a non-admin with no context. And in an org
 * context a platform admin is viewing (#1205): it holds no membership there, so
 * the list is where the org's kind comes from.
 */
export function platformOrgsWanted(gate: Gate, onOrganisations: boolean): boolean {
  if (gate.kind === "no-context") return gate.user.superadmin;
  if (gate.kind === "ready") {
    const { session } = gate;
    const viewingAnOrg = isPlatformViewer(session) && session.workspace_id === null && session.org_id !== null;
    return onOrganisations || viewingAnOrg;
  }
  return false;
}

/** `GET /api/v1/customers`, for the MSP's screen (#417). A refusal stays on it. */
export async function readCustomersInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "customers", customers: await listCustomers(api) });
  } catch (error) {
    dispatch({ type: "customers-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/billing/spend`, for the workspace's Spend page (#632, D6). */
export async function readSpendInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "spend", spend: await readSpend(api) });
  } catch (error) {
    dispatch({ type: "spend-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/members`, the workspace's members on Team & roles (#806). A refusal stays there. */
export async function readMembersInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "members", members: await listMembers(api) });
  } catch (error) {
    dispatch({ type: "members-failed", message: messageOf(error) });
  }
}

/** `GET /invites`, the team's live invitations on the settings surface (#417). */
export async function readInvitesInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "invites", invites: await listInvites(api) });
  } catch (error) {
    dispatch({ type: "invites-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/access-grants`, for the workspace's own screen (#418). A refusal stays on it. */
export async function readAccessInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "access", access: await readGrants(api) });
  } catch (error) {
    dispatch({ type: "access-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/cloud-accounts`, for the accounts screen (#420). A refusal stays on it. */
export async function readAccountsInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "accounts", accounts: await readAccounts(api) });
  } catch (error) {
    dispatch({ type: "accounts-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/alert-sources`, for the sources screen (#422). A refusal stays on it. */
export async function readSourcesInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "sources", sources: await listSources(api) });
  } catch (error) {
    dispatch({ type: "sources-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/provider`, for the settings surface (#422). A refusal stays on it. */
export async function readProviderInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    const read = await readProvider(api);
    dispatch({ type: "provider", provider: read.provider, lastRefusal: read.last_refusal });
  } catch (error) {
    dispatch({ type: "provider-failed", message: messageOf(error) });
  }
}

/** `GET /api/v1/integrations`, for the settings surface (#492). A refusal stays on it. */
export async function readIntegrationsInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "integrations", integrations: await listIntegrations(api) });
  } catch (error) {
    dispatch({ type: "integrations-failed", message: messageOf(error) });
  }
}

/**
 * `GET /api/v1/integrations/{id}/deliveries`, when a person opens a connection's
 * history (#579). A refusal stays on that connection's row and not on the panel: the
 * other connections' health is still readable.
 */
export async function readDeliveriesInto(api: Api, id: string, dispatch: Dispatch): Promise<void> {
  dispatch({ type: "deliveries-reading", connectionId: id });
  try {
    dispatch({ type: "deliveries", connectionId: id, deliveries: await listIntegrationDeliveries(api, id) });
  } catch (error) {
    dispatch({ type: "deliveries-failed", connectionId: id, message: messageOf(error) });
  }
}

/**
 * `GET /api/v1/setup`, for the run list's empty state (#422) and the setup trail
 * (#805). A failure leaves it unread.
 */
export async function readSetupInto(api: Api, dispatch: Dispatch): Promise<void> {
  try {
    dispatch({ type: "setup", setup: await readSetup(api) });
  } catch {
    // The checklist is a courtesy on an empty list; a refusal shows the plain empty state.
  }
}

/**
 * The optional step under the checklist (#602). Its own event, never `setup`'s: a read
 * that fails or is refused answers null, and the checklist above it is untouched.
 */
export async function readIntegrationsStepInto(
  api: Api,
  session: SessionResponse,
  dispatch: Dispatch,
): Promise<void> {
  dispatch({ type: "integrations-step", step: await readIntegrationsStep(api, session) });
}

/** How often the accounts screen re-reads while a stack is pending: the stack takes a minute. */
export const PENDING_POLL_MS = 8000;

export interface ConsoleActions {
  signIn(credentials: { email: string; password: string }): Promise<void>;
  register(account: {
    email: string;
    password: string;
    display_name: string | null;
  }): Promise<void>;
  completeMfa(challenge: { mfa_token: string; method: string; credential: string }): Promise<void>;
  chooseContext(choice: ContextChoice): Promise<void>;
  signOut(): Promise<void>;
  reload(): void;
  loadMoreRuns?(): Promise<void>;
  /** The doors (#415). Each rejects with the route's refusal, for the form beside it. */
  requestReset(email: string): Promise<void>;
  resetPassword(token: string, newPassword: string): Promise<void>;
  acceptInvite(token: string): Promise<void>;
  /** Accept an invitation found in the product rather than in a mail (#440). */
  acceptWaiting(inviteId: string): Promise<void>;
  /** The inbox's older answered items for one group (#497). */
  loadOlderAnswered?(group: string, cursor: string): Promise<void>;
  /** Read the inbox again: an item answered in its drawer moves to its answered list (#808). */
  refreshInbox?(): Promise<void>;
  registerWithInvite(account: {
    email: string;
    password: string;
    display_name: string | null;
    token: string;
  }): Promise<void>;
  /** A signed-in, unverified session asks for the mail again; resolves on "sent". */
  resendVerification(): Promise<void>;
  /** Leave a door's state behind when moving to another screen. */
  leaveDoor(): void;
  /** One org's workspaces, for the platform admin's "Enter" (#701); rejects with the refusal. */
  listOrgWorkspaces(orgId: string): Promise<OrgWorkspaceRow[]>;
  /** The platform lane (#417): make a customer; rejects with the route's refusal. */
  createOrg(body: {
    kind: "customer" | "partner";
    name: string;
    owner_email: string;
    workspace_name: string | null;
    creation_key: string;
  }): Promise<Created>;
  /** The MSP lane (#417): invite a customer, with the capabilities asked for. */
  inviteCustomer(body: {
    name: string;
    admin_email: string;
    capabilities: Capability[];
    creation_key: string;
  }): Promise<Created>;
  /** A colleague into the workspace you are in — a team invitation, not a customer's. */
  inviteMember(body: { email: string; role: string }): Promise<void>;
  revokeInvite(inviteId: string): Promise<void>;
  /**
   * Take a member out of the workspace you are in, or leave it yourself (#584); rejects with
   * the refusal. Leaving takes the session out of the workspace, so the console reads
   * where it now stands.
   */
  removeMember(member: MemberRow): Promise<void>;
  /** What removing a member would stop: their live API keys and invitations (#584). */
  readRemoval(member: MemberRow): Promise<RemovalReach>;
  /** Grant (behind a step-up) or decline one request (#418); rejects with the refusal. */
  answerGrant(
    grantId: string,
    decision: "granted" | "declined",
    stepUp: StepUp | null,
  ): Promise<void>;
  revokeGrant(grantId: string): Promise<void>;
  /** The requester asks a customer again for capabilities with no live row (#418). */
  requestAgain(workspaceId: string, capabilities: Capability[]): Promise<void>;
  /** Start connecting an AWS account: the launch comes back (#420). Rejects with the refusal. */
  startConnection(regions: string[], name?: string | null): Promise<Started>;
  /** Name a connected account, or clear its name with null (#823). Rejects with the refusal. */
  renameAccount(cloudAccountId: string, name: string | null): Promise<void>;
  /** Disconnect a connected account (#828), then read the accounts again. Rejects with the refusal. */
  disconnectAccount(cloudAccountId: string): Promise<void>;
  /** Whether each account can be scanned, as the server answers it (#1274). */
  readScanAvailability?(): Promise<AvailabilityView>;
  /** Start a posture scan of one account (#1274). Rejects with the refusal. */
  runScan?(cloudAccountId: string): Promise<void>;
  /** The same launch again, for a closed tab. */
  relaunch(connectionId: string): Promise<Started>;
  /**
   * Whether the connect flow is open on a started connection (#824): the accounts screen
   * keeps re-reading while it is, whatever state the connection is in, so a stack that
   * reports in after a relaunch of a failed connection is still seen.
   */
  watchConnection(on: boolean): void;
  /** Check again: verify now, or find the role by account id after a lost callback. */
  checkConnection(connectionId: string, accountId: string | null): Promise<Connection>;
  /** Cancel a connection that is not connected yet (#825). Rejects with the refusal. */
  cancelConnection(connectionId: string): Promise<void>;
  /** A new alert source; the answer carries the secret once for the kinds that sign (#422). */
  createSource(body: NewSource): Promise<SourceRow>;
  setSourceActive(id: string, active: boolean): Promise<void>;
  /** Whether an alarm on one source starts an investigation (#816). */
  setSourceAutoInvestigate?(id: string, on: boolean): Promise<void>;
  /** Delete one source (#816); the list and the setup are read again after. */
  deleteSource?(id: string): Promise<void>;
  /** Send one source's sample alarm (#876); the setup is read again after, for its first run. */
  sendTestAlert?(id: string): Promise<TestAlertSent>;
  /** Which alarms start an investigation on one source, read when it is opened (#847). */
  readSourceFilter?(id: string): Promise<SourceFilterRead>;
  /** Replace that filter; the answer is the same read, after the save. */
  saveSourceFilter?(id: string, settings: FilterSettings): Promise<SourceFilterRead>;
  /** Start one source's dry run for some days, or end it with 0 (#849). */
  setSourceDryRun?(id: string, days: number): Promise<SourceFilterRead>;
  /** The daily limit on automatic investigations and the alarms waiting past it (#849). */
  readAutomation?(): Promise<AutomationRead>;
  saveDailyCap?(dailyCap: number | null): Promise<AutomationRead>;
  /** Start a held alarm's investigation now, or dismiss it; both refresh the Inbox. */
  investigateHeld?(id: string): Promise<{ run_id: string | null; joined: boolean }>;
  dismissHeld?(id: string): Promise<void>;
  /** The model provider: the key is sent once and never comes back (#422). */
  setProvider(body: {
    provider: "openai" | "litellm";
    api_base: string;
    api_key: string;
    high_model_id: string;
    low_model_id: string | null;
  }): Promise<ProviderRow>;
  /** Read the provider's model list with the sealed key; invokes no model. */
  checkProvider(): Promise<ProviderCheck>;
  /** A Jira or Slack connection; the credential is sent once and never comes back (#492). */
  /** The connection it made: what the Jira flow's optional test work item is sent from (#614). */
  createIntegration?(body: NewIntegration): Promise<IntegrationRow>;
  /**
   * Register a webhook receiver under a `webhook_create` step-up (#1073). The answer is the
   * only one carrying the secret; it is handed back to the flow and never put in the state.
   */
  registerWebhook?(config: WebhookConfig, stepUp: StepUp): Promise<CreatedWebhook>;
  /**
   * Rotate a receiver's secret under a `webhook_rotate` step-up (#1075). The answer is the only
   * one carrying the new secret; it is handed back to the flow and never put in the state.
   */
  rotateWebhookSecret?(id: string, stepUp: StepUp): Promise<RotatedWebhook>;
  /** Stop new deliveries to one connection (#492). */
  disableIntegration?(id: string): Promise<void>;
  /** Reconnect a disconnected connection, live only if its check passes (#820): the row, or null. */
  enableIntegration?(id: string): Promise<IntegrationRow | null>;
  /** Remove a connection's saved token, and the connection from the list (#820). */
  deleteIntegration?(id: string): Promise<void>;
  /** Change a live connection instead of disconnecting and re-creating it (#580). The row as saved. */
  updateIntegration?(id: string, body: IntegrationPatch): Promise<IntegrationRow>;
  /** Read one connection's recent deliveries, when its row is expanded (#579). */
  readIntegrationDeliveries?(id: string): Promise<void>;
  /** Put one failed or abandoned delivery back on the queue (#819); rejects with the refusal. */
  retryDelivery?(connectionId: string, deliveryId: string): Promise<void>;
  /** Test a connection with its saved credential (#603): the row carrying the answer, or null. */
  testIntegration?(id: string): Promise<IntegrationRow | null>;
  /** Wait for the check a save has just started (#615): the row carrying it, or null. */
  awaitIntegrationCheck?(id: string): Promise<IntegrationRow | null>;
  /** Send one test work item or message from a saved connection (#614). Answers at once. */
  sendTestDelivery?(id: string): Promise<void>;
  /** Send a webhook receiver its test event and wait for its answer (#1076): the row, or null. */
  sendTestEvent?(id: string): Promise<IntegrationRow | null>;
  /** Read Jira's spaces and work types for the flow that connects it (#600). Nothing is kept. */
  jiraLister?: JiraLister;
  /** Read Slack's public channels for the flow that connects it (#604). Nothing is kept. */
  slackLister?: SlackLister;
  /** Step up and write one property one revision on; rejects with the route's refusal. */
  revisePolicy(revision: PolicyRevision, stepUp: StepUp): Promise<void>;
  /** Reread governed requests when the durable run projection changes. */
  refreshDecisions(): Promise<void>;
  /** Answer one consent request; `always` steps up first. Rejects with the route's refusal. */
  answerConsent(requestId: string, answer: ConsentAnswer, stepUp: StepUp | null): Promise<ConsentOutcome | void>;
  /** Step up for this change and approve or decline it. Rejects with the route's refusal. */
  answerChange(
    changeId: string,
    answer: ChangeAnswer,
    stepUp: StepUp,
    reason: string,
  ): Promise<{ outcome: string } | void>;
  /** The factors this session holds, for the step-up dialog (#636, #659). */
  stepUpFactors(): Promise<StepUpFactors>;
  /** Mint the assertion challenge for one step-up purpose (#636, D10). */
  beginPasskey?(purpose: string): Promise<PasskeyChallenge>;
  /** Mint the assertion challenge that answers a login's `mfa_token` (#636, D10). */
  beginPasskeyLogin?(mfaToken: string): Promise<PasskeyChallenge>;
  /** The signed-in user's own passkeys, for Settings (#660). */
  passkeys?: PasskeyActions;
  /** Replacing the signed-in user's authenticator app, for Settings (#746). */
  authenticator?: AuthenticatorActions;
  /** The workspace's API keys, for Your account (#807). */
  apiKeys?: ApiKeyActions;
  /** The signed-in user's sessions, for Your account (#807). */
  sessions?: SessionActions;
  /**
   * Change the signed-in user's password (#807). The route ends every session, this
   * one too, so a success leaves the console signed out; a refusal is thrown.
   */
  changePassword?(currentPassword: string, newPassword: string): Promise<void>;
  /**
   * Ask for the signed-in user's account to be deleted (#1136): a step-up for the
   * request, then the request. A success leaves the console signed out, saying when the
   * account is deleted; a refusal is thrown and nothing changed.
   */
  requestDeletion?(proof: StepUp): Promise<void>;
  /** When a request made now would delete the account, for the dialog to say before asking. */
  wouldDeleteAt?(): Promise<string | null>;
  /** Cancel the request from inside the 14 days; the console then reads the session again. */
  cancelDeletion?(): Promise<void>;
}

export interface Console {
  state: ConsoleState;
  actions: ConsoleActions;
}

/**
 * @param pathname the location the browser is at, so a read a screen needs is
 *   made when that screen is on and not before. The run list is read for every
 *   signed-in screen because the rail's landing page is one navigation away;
 *   the role catalogue is the settings page's alone.
 */
export function useConsole(pathname: string, api: Api = browserApi, search = ""): Console {
  const [state, dispatch] = useReducer(consoleReducer, initialConsoleState);
  const transition = useRef(0);
  const context = contextOf(state.gate);
  const generation = useRef({ context, version: 0 });
  if (generation.current.context !== context) {
    generation.current = { context, version: generation.current.version + 1 };
  }
  const version = generation.current.version;
  // Each request in a scoped action sequence must still belong to its original
  // workspace, including the write after an asynchronous step-up challenge.
  const assertContext = () => {
    if (generation.current.version !== version) {
      throw new Error(
        "The workspace changed. Review the current workspace before trying again.",
      );
    }
  };
  const scopedApi: Api = {
    get: async <T,>(path: string) => {
      assertContext();
      return api.get<T>(path);
    },
    post: async <T,>(path: string, body?: unknown) => {
      assertContext();
      return api.post<T>(path, body);
    },
    put: async <T,>(path: string, body?: unknown) => {
      assertContext();
      return api.put<T>(path, body);
    },
    patch: async <T,>(path: string, body?: unknown) => {
      assertContext();
      return api.patch<T>(path, body);
    },
    delete: async <T,>(path: string, body?: unknown) => {
      assertContext();
      return api.delete<T>(path, body);
    },
  };
  const scopedDispatch: Dispatch = (event) => {
    if (generation.current.version === version) dispatch(event);
  };
  // Session reads can outlive a context switch even if the old context has not
  // rendered yet. This token changes at the user's action, before the cookie does.
  const beginTransition = (): Dispatch => {
    const token = ++transition.current;
    generation.current.version += 1;
    dispatch({ type: "context-changing" });
    return (event) => {
      if (transition.current === token) dispatch(event);
    };
  };
  const [epoch, setEpoch] = useState(0);
  const reload = useCallback(() => {
    dispatch({ type: "reading" });
    setEpoch((previous) => previous + 1);
  }, []);

  // Who is asking. The first read decides the whole gate; a later one runs
  // because something changed it — a sign-in, a context, a `reload`.
  useEffect(() => {
    let live = true;
    const token = transition.current;
    void readSessionInto(api, (event) => {
      if (live && transition.current === token) dispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, epoch]);

  const gate = state.gate;
  // The context the rows belong to: they are re-read when it moves, and not
  // when anything else about the session does.

  // The memberships, once per signed-in session (#414): the picker's list, and
  // the rail's name for the context. Read when the gate first admits the
  // session and not again until a `reload` or a fresh sign-in clears them.
  //
  // **Keyed on the sign-in and the epoch, not on the list being unread.** The
  // read dispatches twice when the list has one entry — the list, then the
  // session the selection produced — and an effect keyed on "unread" would
  // re-run between the two, cancelling the second. The browser suite found
  // that: the unit test drives the read with a plain recorder and could not.
  const signedIn = gate.kind === "no-context" || gate.kind === "ready";
  const membershipsUnread = state.memberships === null && state.membershipsError === null;

  useEffect(() => {
    if (!signedIn || !membershipsUnread) return;
    let live = true;
    const token = transition.current;
    void readMembershipsInto(api, (event) => {
      if (live && transition.current === token) dispatch(event);
    }, gate.kind === "no-context");
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [api, signedIn, epoch]);

  // The invitations waiting for this user (#440). Read on every sign-in and on
  // every `reload`, and **not** gated on being unread: "Check again" on the
  // no-workspace screen is an invitee asking whether their invitation has
  // arrived yet, and a guard that skipped the re-read would answer with the
  // empty list it already had.
  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    const token = transition.current;
    void readWaitingInto(api, (event) => {
      if (live && transition.current === token) dispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, signedIn, epoch]);

  // The inbox (#495): the user's, like the invitations, so read on the sign-in and on
  // every `reload` and not when the context moves. Accepting an invitation re-reads
  // it (`acceptWaiting`); an item answered on its workspace's screen is gone at the
  // next page load.
  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    const token = transition.current;
    void readInboxInto(api, (event) => {
      if (live && transition.current === token) dispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, signedIn, epoch]);

  useEffect(() => {
    if (gate.kind !== "ready" || gate.session.workspace_id === null) return;
    let live = true;
    void readRunsInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch]);

  // The settings page's read. Keyed on the destination rather than run for
  // every screen: it is one route, and a page that is not open has no claim on
  // a workspace's catalogue.
  const route = routeFor(pathname, search);

  // The doors that act on arrival (#415): a verification link, a sign-in link,
  // and the invitation's preview. Keyed on the door and its token, and run once
  // the gate has been read — the invitation page needs to know whether the
  // holder is signed in, and as whom, before it offers anything.
  //
  // A door that acts on arrival, reached by a session whose account is being deleted, is
  // **held** (#1171): the single screen hides it, so it does nothing behind it. It acts
  // once the deletion is cancelled, and not on a sign-out from that screen, which lands at
  // sign-in instead. A form door acts only from its submit, so it is never held.
  // Each door acts once for its token, a re-run of the effect included; an answer is
  // dropped once the location has left that door.
  const gateRead = gate.kind !== "loading";
  const doorId = route.kind === "door" && ON_ARRIVAL_DOORS.includes(route.door) ? route.door : null;
  const doorToken = route.kind === "door" ? route.token : null;
  const doorKey = doorId === null ? null : JSON.stringify([doorId, doorToken]);
  const deletionPending = gate.kind === "deletion-pending";
  const signedOut = gate.kind === "signed-out";
  const [heldDoor, setHeldDoor] = useState<string | null>(null);
  const actedDoor = useRef<string | null>(null);
  const doorNow = useRef(doorKey);
  doorNow.current = doorKey;

  useEffect(() => {
    if (!gateRead || doorKey === null || actedDoor.current === doorKey) return;
    if (deletionPending) {
      setHeldDoor(doorKey);
      return;
    }
    if (signedOut && heldDoor === doorKey) return;
    actedDoor.current = doorKey;
    setHeldDoor(null);
    const dispatchLive = (event: ConsoleEvent) => {
      if (doorNow.current === doorKey) dispatch(event);
    };
    if (doorId === "verify") void confirmVerificationInto(api, doorToken, dispatchLive);
    else if (doorId === "magic") void confirmMagicInto(api, doorToken, dispatchLive);
    else if (doorId === "invite") void previewInviteInto(api, doorToken, dispatchLive);
  }, [api, gateRead, doorKey, doorId, doorToken, deletionPending, signedOut, heldDoor]);
  // Each workspace settings section reads what its own panels show (#804); every
  // one reads the roles, which say what this session may change there.
  const section =
    route.kind === "destination" && (SETTINGS_SECTIONS as readonly string[]).includes(route.id)
      ? (route.id as SettingsSection)
      : null;
  // The two Connections pages that were settings panels (#805) read the roles too:
  // they say who may change the provider and connect an integration.
  const onProvider = route.kind === "destination" && route.id === "provider";
  const onIntegrations = route.kind === "destination" && route.id === "integrations";
  // Your account reads them too: they say who may manage the workspace's API keys (#807).
  const onAccount = route.kind === "destination" && route.id === "account";
  const onSettings = section !== null || onProvider || onIntegrations || onAccount;
  const onTeam = section === "team";
  const onPolicy = section === "policy";

  useEffect(() => {
    if (context === null || !onSettings) return;
    let live = true;
    void readRolesInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onSettings]);

  useEffect(() => {
    if (context === null || !onPolicy) return;
    let live = true;
    void readPolicyInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onPolicy]);

  // The alert sources, on that screen alone (#422).
  const onSources = route.kind === "destination" && route.id === "sources";

  useEffect(() => {
    if (context === null || !onSources) return;
    let live = true;
    void readSourcesInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onSources]);

  // The provider, on its page (#422, #805).
  useEffect(() => {
    if (context === null || !onProvider) return;
    let live = true;
    void readProviderInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onProvider]);

  // The integrations, on their page (#492, #805).
  useEffect(() => {
    if (context === null || !onIntegrations) return;
    let live = true;
    void readIntegrationsInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onIntegrations]);

  // The setup, on the three pages its trail walks (#805): the trail shows only while
  // the workspace is still setting up for its first investigation.
  const onSetupStep =
    route.kind === "destination" && (route.id === "accounts" || route.id === "sources" || route.id === "provider");

  useEffect(() => {
    if (context === null || !onSetupStep) return;
    let live = true;
    void readSetupInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onSetupStep]);

  // The setup checklist under an empty run list (#422): read when the list has come
  // back empty, and the list re-read every few seconds while it stays so, so the
  // first alert's run appears without a reload. A list of test alerts' runs alone is
  // still before the first investigation (#985), so it is read and polled the same way.
  const onRuns = route.kind === "destination" && route.id === "runs";
  const noRealRunListed = state.runs !== null && state.runs.every((run) => run.test === true);
  // A page of tests can sit in front of an older real run, which the setup names: then
  // the first investigation has happened, and polling would only undo "Load more".
  const awaitingFirstRun = noRealRunListed && state.setup?.first_run_id == null;

  useEffect(() => {
    if (context === null || !onRuns || !awaitingFirstRun) return;
    let live = true;
    const dispatchLive = (event: ConsoleEvent) => {
      if (live) scopedDispatch(event);
    };
    void readSetupInto(api, dispatchLive);
    const timer = setInterval(() => {
      void readRunsInto(api, dispatchLive);
      void readSetupInto(api, dispatchLive);
    }, PENDING_POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [api, context, epoch, onRuns, awaitingFirstRun]);

  // The optional step under that checklist (#602), beside the setup read and never
  // waiting on it, keyed on the session so a role change re-reads it (#627).
  useIntegrationsStep(
    api,
    gate.kind === "ready" ? gate.session : null,
    context !== null && onRuns && noRealRunListed,
    epoch,
    (step) => scopedDispatch({ type: "integrations-step", step }),
  );

  // The members and the invitations, beside the roles on Team & roles (#417, #804, #806).
  useEffect(() => {
    if (context === null || !onTeam) return;
    let live = true;
    const deliver = (event: ConsoleEvent) => {
      if (live) scopedDispatch(event);
    };
    void readMembersInto(api, deliver);
    void readInvitesInto(api, deliver);
    return () => {
      live = false;
    };
  }, [api, context, epoch, onTeam]);

  // The Inbox's drawer answers from the read its item's kind names, under the item's
  // own context once that is selected (#808): the same reads its old screen took.
  const inboxView = route.kind === "destination" && route.id === "inbox" ? inboxViewOf(pathname, search) : null;
  const drawerRead: DrawerRead = inboxView?.open ? INBOX_KINDS[inboxView.open.kind].reads : null;
  // Data access also lists the workspace's standing grants, which only a workspace has.
  const standingWanted =
    inboxView?.filter === INBOX_KINDS.consent_request.filter && gate.kind === "ready" && gate.session.workspace_id !== null;

  // The workspace's access grants, on that screen, and in the drawer (#418, #808).
  const onAccess = (route.kind === "destination" && route.id === "access") || drawerRead === "access";

  useEffect(() => {
    if (context === null || !onAccess) return;
    let live = true;
    void readAccessInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onAccess]);

  // The workspace's AWS accounts, on that screen alone (#420) — re-read every
  // few seconds while a connection waits on its stack, so the screen moves when
  // the callback lands without a person pressing anything.
  const onAccounts = route.kind === "destination" && route.id === "accounts";
  const [watching, setWatching] = useState(false);
  const pending =
    watching ||
    (state.accounts?.connections.some(
      (c) => c.state === "awaiting_stack" || c.state === "verifying",
    ) ?? false);

  useEffect(() => {
    if (context === null || !onAccounts) return;
    let live = true;
    const read = () =>
      void readAccountsInto(api, (event) => {
        if (live) scopedDispatch(event);
      });
    read();
    const timer = pending ? setInterval(read, PENDING_POLL_MS) : null;
    return () => {
      live = false;
      if (timer !== null) clearInterval(timer);
    };
  }, [api, context, epoch, onAccounts, pending]);

  // The roles too, on that screen: whether this session may connect is its role's (#824).
  useEffect(() => {
    if (context === null || !onAccounts) return;
    let live = true;
    void readRolesInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onAccounts]);

  // The MSP's customers, on that screen alone (#417).
  const onCustomers =
    currentDestination(route, gate.kind === "ready" ? gate.session : undefined) === "customers";

  useEffect(() => {
    if (context === null || !onCustomers) return;
    let live = true;
    void readCustomersInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onCustomers]);

  // The workspace's own spend, on that screen alone (#632, D6).
  const onSpend = route.kind === "destination" && route.id === "spend";

  useEffect(() => {
    if (context === null || !onSpend) return;
    let live = true;
    void readSpendInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onSpend]);

  // The platform admin's customers, on that screen alone (#417). Keyed on the
  // sign-in rather than the context: the list is every org, whichever the
  // admin happens to be reading under.
  const onOrganisations = route.kind === "destination" && route.id === "organisations";
  const orgsWanted = platformOrgsWanted(gate, onOrganisations);

  useEffect(() => {
    if (!orgsWanted) return;
    let live = true;
    void readOrgsInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, orgsWanted, epoch]);

  // Consent reads are shared by the queue and investigation decision area.
  const onConsent = route.kind === "run" || drawerRead === "consent" || standingWanted;

  useEffect(() => {
    if (context === null || !onConsent) return;
    let live = true;
    void readConsentInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onConsent]);

  // Changes are read for both the queue and the investigation decision area.
  const onChanges = route.kind === "run" || drawerRead === "changes";

  useEffect(() => {
    if (context === null || !onChanges) return;
    let live = true;
    void readChangesInto(api, (event) => {
      if (live) scopedDispatch(event);
    });
    return () => {
      live = false;
    };
  }, [api, context, epoch, onChanges]);

  const applyLogin = useCallback(
    async (result: LoginResponse) => {
      if (result.mfa_required) {
        dispatch({ type: "mfa", mfaToken: result.mfa_token, methods: result.methods });
        return;
      }
      // The cookie is set; `GET /auth/session` is how the console learns the
      // context, which the login body does not carry.
      dispatch(await sessionEvent(api));
    },
    [api],
  );

  const actions: ConsoleActions = {
    signIn: async (credentials) => applyLogin(await signIn(api, credentials)),
    register: async (account) => applyLogin(await register(api, account)),
    completeMfa: async (challenge) =>
      doorId === "magic"
        ? completeMagicMfaInto(api, challenge, dispatch)
        : applyLogin(await completeMfa(api, challenge)),
    chooseContext: async (choice) => {
      const deliver = beginTransition();
      try {
        await selectContext(api, choice);
        await readSessionInto(api, deliver);
      } catch (error) {
        await readSessionInto(api, deliver);
        // The one refusal the control plane gives alike for a context that does not exist
        // and one not held (`auth/service.py::select_context`); another reason keeps its words.
        const refused = error instanceof ApiError && detailOf(error.body) === "no membership in that context";
        deliver({ type: "context-failed", message: messageOf(error), refused });
        throw error;
      }
    },
    signOut: async () => {
      const deliver = beginTransition();
      try {
        await signOut(api);
        deliver({ type: "signed-out", notice: SIGNED_OUT_NOTICE });
      } catch (error) {
        await readSessionInto(api, deliver);
        deliver({ type: "context-failed", message: messageOf(error) });
      }
    },
    reload,
    loadMoreRuns: async () => {
      if (state.runsLoadingMore || !state.runsHasMore) return;
      const before = state.runs?.[state.runs.length - 1]?.dispatched_at;
      if (!before) return;
      scopedDispatch({ type: "runs-loading-more" });
      try {
        const runs = await listRuns(api, { before, limit: 50 });
        scopedDispatch({ type: "runs-more", runs });
      } catch (error) {
        if (refusalNeedsSessionRead(error)) await readSessionInto(api, scopedDispatch);
        scopedDispatch({ type: "runs-failed", message: messageOf(error) });
      }
    },
    requestReset: (email) => requestResetInto(api, email, dispatch),
    resetPassword: (token, newPassword) => resetPasswordInto(api, token, newPassword, dispatch),
    acceptInvite: (token) => acceptInviteInto(api, token, dispatch),
    loadOlderAnswered: (group, cursor) => readOlderAnsweredInto(api, group, cursor, dispatch),
    refreshInbox: () => readInboxInto(api, dispatch),
    acceptWaiting: async (inviteId) => {
      const deliver = beginTransition();
      try {
        await acceptWaitingInto(api, inviteId, deliver);
        await readInboxInto(api, deliver);
      } catch (error) {
        await readSessionInto(api, deliver);
        deliver({ type: "context-failed", message: messageOf(error) });
        throw error;
      }
    },
    registerWithInvite: (account) => registerWithInviteInto(api, account, dispatch),
    resendVerification: async () => {
      await requestEmailVerification(api);
    },
    leaveDoor: () => dispatch({ type: "door", door: null }),
    listOrgWorkspaces: (orgId) => listOrgWorkspaces(api, orgId),
    createOrg: async (body) => {
      const created = await createOrg(scopedApi, body);
      await readOrgsInto(scopedApi, scopedDispatch);
      return created;
    },
    inviteCustomer: async (body) => {
      const created = await inviteCustomer(scopedApi, body);
      await readCustomersInto(scopedApi, scopedDispatch);
      return created;
    },
    inviteMember: async (body) => {
      await inviteMember(scopedApi, body);
      await readInvitesInto(scopedApi, scopedDispatch);
    },
    revokeInvite: async (inviteId) => {
      await revokeInvite(scopedApi, inviteId);
      await readInvitesInto(scopedApi, scopedDispatch);
    },
    readRemoval: (member) => readRemoval(scopedApi, member.user_id),
    removeMember: async (member) => {
      await removeMember(scopedApi, member.user_id);
      if (gate.kind !== "ready" || gate.session.user.user_id !== member.user_id) {
        await readMembersInto(scopedApi, scopedDispatch);
        return;
      }
      const deliver = beginTransition();
      await readMembershipsInto(api, deliver, false);
      await readSessionInto(api, deliver);
    },
    answerGrant: async (grantId, decision, stepUp) => {
      await answerGrant(scopedApi, grantId, decision, stepUp);
      await readAccessInto(scopedApi, scopedDispatch);
    },
    revokeGrant: async (grantId) => {
      await revokeGrant(scopedApi, grantId);
      await readAccessInto(scopedApi, scopedDispatch);
    },
    requestAgain: async (workspaceId, capabilities) => {
      await requestAgain(scopedApi, workspaceId, capabilities);
      await readCustomersInto(scopedApi, scopedDispatch);
    },
    startConnection: async (regions, name = null) => {
      const started = await startConnection(scopedApi, regions, name);
      await readAccountsInto(scopedApi, scopedDispatch);
      return started;
    },
    renameAccount: async (cloudAccountId, name) => {
      await renameAccount(scopedApi, cloudAccountId, name);
      await readAccountsInto(scopedApi, scopedDispatch);
    },
    disconnectAccount: async (cloudAccountId) => {
      await disconnectAccount(scopedApi, cloudAccountId);
      await readAccountsInto(scopedApi, scopedDispatch);
    },
    readScanAvailability: () => readAvailability(scopedApi),
    runScan: async (cloudAccountId) => {
      await startScan(scopedApi, cloudAccountId);
    },
    relaunch: async (connectionId) => {
      const started = await relaunch(scopedApi, connectionId);
      await readAccountsInto(scopedApi, scopedDispatch);
      return started;
    },
    watchConnection: setWatching,
    checkConnection: async (connectionId, accountId) => {
      const checked = await checkConnection(scopedApi, connectionId, accountId);
      await readAccountsInto(scopedApi, scopedDispatch);
      return checked;
    },
    cancelConnection: async (connectionId) => {
      await cancelConnection(scopedApi, connectionId);
      await readAccountsInto(scopedApi, scopedDispatch);
    },
    createSource: async (body) => {
      const created = await createSource(scopedApi, body);
      await readSourcesInto(scopedApi, scopedDispatch);
      return created;
    },
    setSourceActive: async (id, active) => {
      await setSourceActive(scopedApi, id, active);
      await readSourcesInto(scopedApi, scopedDispatch);
    },
    setSourceAutoInvestigate: async (id, on) => {
      await setSourceAutoInvestigate(scopedApi, id, on);
      await readSourcesInto(scopedApi, scopedDispatch);
    },
    deleteSource: async (id) => {
      await deleteSource(scopedApi, id);
      await Promise.all([readSourcesInto(scopedApi, scopedDispatch), readSetupInto(scopedApi, scopedDispatch)]);
    },
    sendTestAlert: async (id) => {
      const sent = await sendTestAlert(scopedApi, id);
      await readSetupInto(scopedApi, scopedDispatch);
      return sent;
    },
    readSourceFilter: (id) => readSourceFilter(scopedApi, id),
    saveSourceFilter: (id, settings) => saveSourceFilter(scopedApi, id, settings),
    setSourceDryRun: async (id, days) => {
      const read = await setSourceDryRun(scopedApi, id, days);
      await readSourcesInto(scopedApi, scopedDispatch);
      return read;
    },
    readAutomation: () => readAutomation(scopedApi),
    saveDailyCap: (dailyCap) => saveDailyCap(scopedApi, dailyCap),
    investigateHeld: (id) => investigateHeld(scopedApi, id),
    dismissHeld: (id) => dismissHeld(scopedApi, id),
    setProvider: async (body) => {
      const row = await setProvider(scopedApi, body);
      // A saved provider is a new one (#1415): no refusal of the one it replaced is its.
      scopedDispatch({ type: "provider", provider: row, lastRefusal: null });
      return row;
    },
    checkProvider: () => checkProvider(scopedApi),
    createIntegration: async (body) => {
      const created = await createIntegration(scopedApi, body);
      await readIntegrationsInto(scopedApi, scopedDispatch);
      return created;
    },
    // The list is read again, and carries the secret's prefix only.
    registerWebhook: async (config, stepUp) => {
      const created = await registerWebhook(scopedApi, config, stepUp);
      await readIntegrationsInto(scopedApi, scopedDispatch);
      return created;
    },
    // The list is read again, and carries the new prefix and the overlap's end only.
    rotateWebhookSecret: async (id, stepUp) => {
      const rotated = await rotateWebhookSecret(scopedApi, id, stepUp);
      await readIntegrationsInto(scopedApi, scopedDispatch);
      return rotated;
    },
    jiraLister: {
      spaces: (asking) => listJiraSpaces(scopedApi, asking),
      workTypes: (asking) => listJiraWorkTypes(scopedApi, asking),
    },
    slackLister: {
      channels: (asking) => listSlackChannels(scopedApi, asking),
    },
    sendTestDelivery: async (id) => {
      const before = (await sendTestDelivery(scopedApi, id)).last_check?.checked_at ?? null;
      // Not awaited: the call has answered, which is all the flow waits for. The Health
      // column catches up once Jira or Slack has, and a read that fails is the next load's.
      void newCheckAfter(scopedApi, id, before, { wanted: "test_delivery" })
        .then(() => readIntegrationsInto(scopedApi, scopedDispatch))
        .catch(() => {});
    },
    sendTestEvent: async (id) => {
      try {
        return await sendTestEvent(scopedApi, id);
      } finally {
        // Whatever the wait saw, the list is what the Health column reads.
        await readIntegrationsInto(scopedApi, scopedDispatch);
      }
    },
    disableIntegration: async (id) => {
      await disableIntegration(scopedApi, id);
      await readIntegrationsInto(scopedApi, scopedDispatch);
    },
    enableIntegration: async (id) => {
      try {
        return await enableConnection(scopedApi, id);
      } finally {
        await readIntegrationsInto(scopedApi, scopedDispatch);
      }
    },
    deleteIntegration: async (id) => {
      await deleteIntegration(scopedApi, id);
      await readIntegrationsInto(scopedApi, scopedDispatch);
    },
    // The list is re-read after the write, so the row's health line is the one the
    // edit produced and not the one that sent the person to the form (#579, #580).
    updateIntegration: async (id, body) => {
      const saved = await updateIntegration(scopedApi, id, body);
      await readIntegrationsInto(scopedApi, scopedDispatch);
      return saved;
    },
    readIntegrationDeliveries: (id) => readDeliveriesInto(scopedApi, id, scopedDispatch),
    // The row as the route answered it, in its place, and the list read again, because the
    // connection's health is read from its newest delivery and this one just moved (#819).
    retryDelivery: async (connectionId, deliveryId) => {
      const delivery = await retryDelivery(scopedApi, connectionId, deliveryId);
      scopedDispatch({ type: "delivery-retried", connectionId, delivery });
      await readIntegrationsInto(scopedApi, scopedDispatch);
    },
    testIntegration: async (id) => {
      try {
        return await testConnection(scopedApi, id);
      } finally {
        // Whatever the wait saw, the list is what the Health column reads.
        await readIntegrationsInto(scopedApi, scopedDispatch);
      }
    },
    // A save's check is waited for as a test's answer is, and the list read again after (#615).
    awaitIntegrationCheck: async (id) => {
      try {
        return await savedCheck(scopedApi, id);
      } finally {
        await readIntegrationsInto(scopedApi, scopedDispatch);
      }
    },
    revisePolicy: async (revision, stepUp) => {
      try {
        await revisePolicy(scopedApi, revision, stepUp);
      } catch (error) {
        if (error instanceof ApiError && error.status === 409 && detailOf(error.body) === "policy_revision_changed") {
          // Keep the editor mounted: replace only its authoritative revision read.
          // A failed refresh must not silently turn a retry into a blind overwrite.
          try {
            scopedDispatch({ type: "policy", policy: await readPolicy(scopedApi) });
          } catch {
            throw new Error("The policy changed, but its latest revision could not be read. Your entries are kept. Check your connection and retry.");
          }
          throw new Error("The policy changed since you read it. The current revision is shown above. Review it, then save again to apply your entries.");
        }
        throw error;
      }
      // The page shows what the workspace now holds, read back, not what was sent.
      await readPolicyInto(scopedApi, scopedDispatch);
    },
    refreshDecisions: async () => {
      await Promise.all([readChangesInto(scopedApi, scopedDispatch), readConsentInto(scopedApi, scopedDispatch)]);
    },
    answerConsent: async (requestId, answer, stepUp) => {
      const outcome = await answerConsent(scopedApi, requestId, answer, stepUp);
      // Read back: the answered request leaves the list, a standing grant joins its own.
      await readConsentInto(scopedApi, scopedDispatch);
      return outcome;
    },
    answerChange: async (changeId, answer, stepUp, reason) => {
      const outcome = await answerChange(scopedApi, changeId, answer, stepUp, reason);
      // Read back: the answered change leaves the screen.
      await readChangesInto(scopedApi, scopedDispatch);
      return outcome;
    },
    stepUpFactors: () => readStepUpFactors(scopedApi),
    beginPasskey: (purpose) => beginPasskeyAuthentication(scopedApi, purpose),
    beginPasskeyLogin: (mfaToken) => beginPasskeyLogin(scopedApi, mfaToken),
    passkeys: passkeyActions(scopedApi),
    authenticator: authenticatorActions(scopedApi),
    apiKeys: apiKeyActions(scopedApi),
    sessions: sessionActions(scopedApi),
    changePassword: async (currentPassword, newPassword) => {
      await changePassword(api, currentPassword, newPassword);
      beginTransition()({ type: "signed-out", notice: PASSWORD_CHANGED_NOTICE });
    },
    requestDeletion: async (proof) => {
      const grant = await mintStepUpGrant(scopedApi, proof, ACCOUNT_DELETE_PURPOSE);
      const deletesAt = await requestDeletion(scopedApi, grant);
      beginTransition()({ type: "signed-out", notice: deletionRecordedNotice(deletesAt) });
    },
    wouldDeleteAt: () => readWouldDeleteAt(scopedApi),
    cancelDeletion: async () => {
      await cancelDeletion(api);
      await readSessionInto(api, dispatch);
    },
  };

  // A link into the console (#266) — a change, or a run — selects the context it names,
  // once per link: a refusal is presented by the context transition and not retried.
  const linked = linkedContextOf(pathname, search);
  const linkChoice = contextToFollow(linked, gate);
  const linkKey = linkChoice === null ? null : JSON.stringify(linkChoice);
  const followed = useRef<string | null>(null);
  // A location that names no context ends the link: the Inbox's drawer closed, say
  // (#808). The next item that names one is followed again, even the same context —
  // the person may have moved away from it in between. Not reset while the location
  // still names it, so a refused context is not retried.
  if (linked === null) followed.current = null;
  useEffect(() => {
    if (linkChoice === null || followed.current === linkKey) return;
    followed.current = linkKey;
    void actions.chooseContext(linkChoice).then(
      // Where the link landed, for the drawer to say so (#858): the console stays there.
      () => dispatch({ type: "link-arrived", context: linkChoice }),
      () => {
        /* The context transition presents the server's refusal. */
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the link's choice alone
  }, [linkKey]);
  // A location that names no context has nothing left to say it landed in.
  const unlinked = linked === null;
  useEffect(() => {
    if (unlinked) dispatch({ type: "link-arrived", context: null });
  }, [unlinked]);

  const doorHeld = heldDoor !== null && heldDoor === doorKey;
  return { state: doorHeld ? { ...state, doorHeld } : state, actions };
}

/**
 * The context a link asks this session to select, or null when there is none to select:
 * no link, a gate still being read or signed out, or the session already there. A
 * session with no context yet is taken straight to the link's, past the picker.
 */
export function contextToFollow(linked: ContextChoice | null, gate: Gate): ContextChoice | null {
  if (linked === null) return null;
  if (gate.kind === "no-context") return linked;
  if (gate.kind === "ready") return inLinkContext(gate.session, linked) ? null : linked;
  return null;
}
